import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime, Duration } from 'luxon';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Button from '@mui/material/Button';

import TrashcanIcon from '@mui/icons-material/Delete';

import { setClientsInfo, removeClientFromClientsInfo } from '../store/slices/clientsInfoSlice';
import { addPersons } from '../store/slices/personsSlice';
import { updateClientsFilter, setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { setPropertyFilterState, setPersonFilterState } from '../store/slices/filterStatesSlice';
import { clearVobFormInfos } from '../store/slices/vobFormInfosSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  isNonLocalNonLoggedInVisitor,
  reportTime,
  dateStringForDateTime,
  dateStringForDateTimeIfLaterThan,
  getPropertyFilterMenuItems,
  getDefaultPropertyFilterMenuItems,
  getUserFilterMenuItems,
  getDefaultUserFilterMenuItems,
  allMenuItemsAreChecked,
  filterIsInDefaultState,
} from '../utils/utils';
import createHcmForPageAndDescriptions from '../utils/HideableColumnManager';

import SocketAccess from '../comm/SocketAccess';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import ClientTableAppBar from './ClientTableAppBar';
import ColumnFilterIcon from './ColumnFilterIcon';
import KSpinner from './KSpinner';
import ConfirmModal from './ConfirmModal';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

function userIsAdmin() {
  return DA.getLoggedInUserInfo().role === 'Admin';
}

function desc(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    aValue = aValue ? aValue.toLowerCase() : '';
    bValue = bValue ? bValue.toLowerCase() : '';
  }
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparisonFunction(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function handleEntered() {
  document.getElementsByTagName('body')[0].style.paddingRight = '0px';
}

function buildColumns(shownStatus, isFirmSyncedWithEmr) {
  const showingProspects = shownStatus === 'Prospect';
  let columns = [
    { id: 'preferredName', numeric: false, label: 'First Name' },
    { id: 'lastName', numeric: false, label: 'Last Name' },
    { id: 'createdDate', numeric: false, label: 'Date Created' },
    { id: 'phone', notSortable: true, numeric: false, label: 'Phone', hideable: true, initiallyShown: true },
  ];
  if (showingProspects) {
    columns = [
      ...columns,
      { id: 'leadTemperature', filterable: true, numeric: false, label: 'Temp', hideable: true, initiallyShown: true },
      {
        id: 'salesStage',
        filterable: true,
        numeric: false,
        label: 'Sales Stage',
        hideable: true,
        initiallyShown: true,
      },
    ];
  }
  if (isFirmSyncedWithEmr) {
    columns = [
      ...columns,
      { id: 'facilityName', filterable: true, numeric: false, label: 'Facility', hideable: true, initiallyShown: true },
    ];
  }
  columns = [
    ...columns,
    { id: 'ownerName', filterable: true, numeric: false, label: 'Owner', hideable: true, initiallyShown: true },
    { id: 'latestContactDate', numeric: true, label: 'Last Contact', hideable: true, initiallyShown: true },
  ];
  if (userIsAdmin()) {
    columns = [...columns, { id: 'actions', notSortable: true, numeric: false, label: 'Action' }];
  }
  return columns;
}

function getPageName(shownStatus) {
  switch (shownStatus) {
    case 'Prospect':
      return 'prospectsPage';
    case 'Client':
      return 'clientsPage';
    case 'Alum':
      return 'alumniPage';
    default:
      return 'prospectsPage';
  }
}

class ClientTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  createFilterHandler = (property) => (event) => {
    this.props.onRequestFilter(event, property);
  };

  render() {
    const { classes, hcm, order, orderBy, shouldHighlightFilterIcon } = this.props; // for sorting

    const visibleColumnDescrips = hcm ? hcm.visibleColumnDescrips : [];

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {visibleColumnDescrips.map(
            (column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.filterable && (
                  <ColumnFilterIcon
                    onClick={this.createFilterHandler(column.id)}
                    shouldHighlight={() => shouldHighlightFilterIcon(column.id)}
                  />
                )}

                {column.label}

                {!column.notSortable && (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={this.createSortHandler(column.id)}
                      classes={{
                        icon: orderBy === column.id ? classes.activeSortIcon : classes.inactiveSortIcon,
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}
// END OF ClientTableHead

ClientTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestFilter: PropTypes.func.isRequired,
};

const StyledTableHead = connect(null, null, null, { forwardRef: true })(withStyles(tableHeadStyles)(ClientTableHead));

const styles = (theme) => ({
  ...pageTableStyles(theme),
  actionButton: {
    margin: '0 0 0 5px',
    padding: '5px',
    '& svg,& .fab,& .fas,& .far,& .fal,& .material-icons': {
      marginRight: '0px',
    },
  },
  icon: {
    verticalAlign: 'middle',
    width: '17px',
    height: '17px',
    top: '-1px',
    position: 'relative',
  },
  filterListItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  selectAllButton: {
    textTransform: 'none',
    fontSize: '18px',
  },
  filterLabel: {
    color: '#6b6b6b',
  },
});

let allColumnDescrips = [];

class ClientTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      redisplayToggle: false,
      order: 'asc',
      orderBy: 'lastName',
      data: [],
      goToNewProspectPage: false,
      showFilter: false,
      filterItemList: [],
      filterBy: '',
      shouldShowDeletionConfirmationModal: false,
      ...this.props.reduxFilterMenuState,
    };

    allColumnDescrips = buildColumns(this.props.shownStatus, this.props.emrInfo.isSyncedWithFirm);
  }

  ownerMenuItems = this.getOwnerMenuItems();
  temperatureMenuItems = this.getTemperatureMenuItems();
  salesStageMenuItems = this.getSalesStageMenuItems();
  facilityMenuItems = this.getFacilitiesMenuItems();

  defaultTemperatureMenuItems = this.getDefaultMenuItems('Prospect Status');
  defaultSalesStageMenuItems = this.getDefaultMenuItems('Sales Stage');
  defaultOwnerMenuItems = this.getDefaultMenuItems('Owner');
  defaultFacilityMenuItems = this.getDefaultFacilitiesMenuItems();

  deletionConfirmationQuery = 'Are you sure you want to delete this demo?';

  componentDidMount() {
    this.ownerMenuItems = this.getOwnerMenuItems();
    this.temperatureMenuItems = this.getTemperatureMenuItems();
    this.salesStageMenuItems = this.getSalesStageMenuItems();

    const pageName = getPageName(this.props.shownStatus);
    this.hcm = createHcmForPageAndDescriptions(pageName, allColumnDescrips);

    this.hcm.loadColumnVisibilityFromRedux();

    this.fetchClientsInformationAndRender(true);

    const personCallbackStruct = {
      kind: 'person',
      pageName: 'ClientTable',
      refreshFunc: () => this.redisplayTable(),
    };
    SocketAccess.registerRefreshCallback(personCallbackStruct);

    const statusTransitionCallbackStruct = {
      kind: 'statusTransition',
      pageName: 'ClientTable',
      refreshFunc: () => this.redisplayTable(),
    };
    SocketAccess.registerRefreshCallback(statusTransitionCallbackStruct);

    const clientCallbackStruct = {
      kind: 'client',
      pageName: 'ClientTable',
      refreshFunc: () => this.redisplayTable(),
    };
    SocketAccess.registerRefreshCallback(clientCallbackStruct);
  }

  componentWillUnmount() {
    this.hcm.constructAndSaveReduxState();
    SocketAccess.unregisterRefreshCallbacksForPage('ClientTable');
  }

  redisplayTable() {
    console.log('In ClientTable.redisplayTable()');
    this.setState({ redisplayToggle: !this.state.redisplayToggle });
  }

  fetchClientsInformationAndRender(showLoading) {
    const firmId = DA.getFirmId();

    if (DA.haveClientsInfoForFirm(firmId)) {
      this.setState({ loading: false });
      return;
    }

    const userId = this.props.userInfo.userId;
    const haveUserId = userId && userId.length > 10;
    const haveFirmId = firmId && firmId > 0;
    if (haveUserId && haveFirmId) {
      if (showLoading) {
        this.setState({ loading: true });
      }
      const goodResponseAction = (jsonArray) => {
        reportTime('ftchClents', b4Fetch);
        const clients = jsonArray;

        const b4ReduxConvert = DateTime.local();
        const reduxifiedClients = clients.map((c) =>
          ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(c)
        );
        reportTime('reduxCnvrt', b4ReduxConvert);

        const b4ReduxStore = DateTime.local();
        this.props.addPersons(reduxifiedClients);
        reportTime('reduxStore', b4ReduxStore);

        const clientIds = clients.map((c) => c.personId);
        const clientsInfo = {
          firmId,
          nClients: clients.length,
          clientIds: clientIds,
        };
        this.props.setClientsInfo(clientsInfo);
        this.setState({ loading: false });
      };
      const uri = '/api/firm/' + firmId + '/clientsInfo';
      const request = createStdRequest(uri);
      const b4Fetch = DateTime.local();
      apiFetch(this, request, goodResponseAction, '[GET] /api/firm/<firmId>/clientsInfo');
    } else {
      const fakeClientsInfo = { firmId: 0, nClients: 0, clientIds: [] };
      this.props.setClientsInfo(fakeClientsInfo);
      this.setState({ loading: false });
    }
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  showFilterMenu = (event, columnId) => {
    const { clientX, clientY } = event;
    let filterItemList = [];

    if (columnId === 'salesStage') {
      filterItemList = this.salesStageMenuItems;
    } else if (columnId === 'leadTemperature') {
      filterItemList = this.temperatureMenuItems;
    } else if (columnId === 'ownerName') {
      filterItemList = this.ownerMenuItems;
    } else if (columnId === 'facilityName') {
      filterItemList = this.facilityMenuItems;
    }
    this.filteredColumnId = columnId;
    this.setState({
      showFilter: true,
      filterX: clientX,
      filterY: clientY,
      filterItemList,
      filterBy: columnId,
    });
  };

  getOwnerMenuItems() {
    const pageName = getPageName();
    const pageFiltersState = this.props.reduxPropertyFiltersState[pageName];
    return getUserFilterMenuItems(pageFiltersState, 'owner');
  }

  getSalesStageMenuItems() {
    if (this.props.shownStatus !== 'Prospect') {
      return null;
    }

    const pageFiltersState = this.props.reduxPropertyFiltersState['prospectsPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'salesStage', 'Sales Stage');
  }

  getTemperatureMenuItems() {
    if (this.props.shownStatus !== 'Prospect') {
      return null;
    }

    const pageFiltersState = this.props.reduxPropertyFiltersState['prospectsPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'leadTemperature', 'Prospect Status');
  }

  getFacilitiesMenuItems() {
    const pageName = getPageName();
    const pageFiltersState = this.props.reduxPropertyFiltersState[pageName];
    const cachedNameValuePairs = pageFiltersState ? pageFiltersState['facility'] || [] : [];
    const cachedNames = cachedNameValuePairs.map((pair) => pair.name);

    const facilities = DA.getAllEmrFacilities();
    let facilityNames = facilities.map((facility) => facility.name);
    facilityNames = ['Unassigned', ...facilityNames];
    const items = facilityNames.map((facilityName) => {
      const matchingPair = cachedNameValuePairs.find((pair) => pair.name === facilityName);
      const haveCachedValue = cachedNames.indexOf(facilityName) >= 0;
      const checked = haveCachedValue ? matchingPair.value : true;
      return {
        name: facilityName,
        checked,
      };
    });
    const allSelected = allMenuItemsAreChecked(items);
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    return [{ name: selectionButtonLabel }, ...items];
  }

  getDefaultMenuItems(dropdownName) {
    if (dropdownName !== 'Owner') {
      return getDefaultPropertyFilterMenuItems(dropdownName);
    } else {
      return getDefaultUserFilterMenuItems();
    }
  }

  getDefaultFacilitiesMenuItems() {
    const facilities = DA.getAllEmrFacilities();
    let facilityNames = facilities.map((facility) => facility.name);
    facilityNames = ['Unassigned', ...facilityNames];
    const items = facilityNames.map((facilityName) => {
      return {
        name: facilityName,
        checked: true,
      };
    });
    const allSelected = allMenuItemsAreChecked(items);
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    return [{ name: selectionButtonLabel }, ...items];
  }

  handleFilterItemChange = (itemIndex) => {
    const copiedList = this.state.filterItemList.slice(0);
    copiedList[itemIndex].checked = !this.state.filterItemList[itemIndex].checked;

    this.saveMenuItemState(copiedList[itemIndex]);

    const allSelected = allMenuItemsAreChecked(copiedList.slice(1));
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    copiedList[0].name = selectionButtonLabel;

    this.setState({ filterItemList: copiedList });
  };

  shouldHighlightFilterIcon(columnId) {
    if (columnId === 'leadTemperature') {
      return this.shouldHighlightTemperatureFilterIcon();
    } else if (columnId === 'salesStage') {
      return this.shouldHighlightSalesStateFilterIcon();
    } else if (columnId === 'ownerName') {
      return this.shouldHighlighOwnerFilterIcon();
    }
  }

  shouldHighlightTemperatureFilterIcon() {
    return !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems);
  }

  shouldHighlightSalesStateFilterIcon() {
    return !filterIsInDefaultState(this.salesStageMenuItems, this.defaultSalesStageMenuItems);
  }

  shouldHighlighOwnerFilterIcon() {
    return !filterIsInDefaultState(this.ownerMenuItems, this.defaultOwnerMenuItems);
  }

  saveMenuItemState(item) {
    const columnId = this.filteredColumnId;
    const pageName = getPageName();
    if (columnId === 'ownerName') {
      const payload = {
        pageName,
        filterName: columnId,
        personId: item.id,
        personName: item.name,
        value: item.checked,
      };
      this.props.setPersonFilterState(payload);
    } else if (columnId === 'leadTemperature') {
      const payload = {
        pageName,
        filterName: columnId,
        propertyName: item.name,
        value: item.checked,
      };
      this.props.setPropertyFilterState(payload);
    } else if (columnId === 'salesStage') {
      const payload = {
        pageName,
        filterName: columnId,
        propertyName: item.name,
        value: item.checked,
      };
      this.props.setPropertyFilterState(payload);
    } else if (columnId === 'facilityName') {
      const payload = {
        pageName,
        filterName: columnId,
        propertyName: item.name,
        value: item.checked,
      };
      this.props.setPropertyFilterState(payload);
    }
  }

  handleSelectAllButtonClick = (itemName) => {
    const copiedList = this.state.filterItemList.slice(0);
    let shouldBeChecked;
    if (itemName === 'Select All') {
      copiedList[0].name = 'Unselect All';
      shouldBeChecked = true;
    } else {
      copiedList[0].name = 'Select All';
      shouldBeChecked = false;
    }

    copiedList.forEach((item) => {
      if (!item.name.endsWith('elect All')) {
        item.checked = shouldBeChecked;
      }
      this.saveMenuItemState(item);
    });

    this.setState({ filterItemList: copiedList });
  };

  handleFilterClickAway = () => {
    this.setState({ showFilter: false });
  };

  handleClickOnClientName = (id) => {
    this.props.setCaseIntendedClientId(id);
    this.setState({ redirect: 'case' });
  };

  handleClickOnDeleteIcon = (id) => {
    this.confirmAndMaybeDeleteClient(id);
  };

  confirmAndMaybeDeleteClient(id) {
    const clientName = DA.getPersonById(id).preferredFullName;
    this.deletionConfirmationQuery = `Are you sure you want to delete ${clientName}?`;
    this.deleteClientFunc = () => this.deleteClient(id);
    this.setState({ shouldShowDeletionConfirmationModal: true });
  }

  deleteClient(id) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/prospect/${id}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = (json) => {
      // TODO perhaps this should actually be done via WebSocket calls from API
      this.setState({ shouldShowDeletionConfirmationModal: false });
      this.props.removeClientFromClientsInfo(id);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  applyFilter(entries) {
    return entries.filter((e) => this.passFilter(e));
  }

  passFilter(entry) {
    if (!entry || !this.hcm) {
      return false;
    }

    const statusIsRight = entry.status === this.props.shownStatus;
    if (!statusIsRight) {
      return false;
    }

    const ownerPasses = !this.hcm.columnIsVisible('ownerName') || this.isOwnerMenuItemChecked(entry.ownerId);
    if (!ownerPasses) {
      console.log(`Filtering person ${entry.personId} failed on ownerName. OwnerId: **${entry.ownerId}**`);
      return false;
    }

    if (entry.status === 'Prospect') {
      const tempPasses =
        !this.hcm.columnIsVisible('leadTemperature') || this.isTemperatureMenuItemChecked(entry.leadTemperature);
      if (!tempPasses) {
        console.log(`Filtering person ${entry.personId} failed on leadTemperature. Temp: **${entry.leadTemperature}**`);
        return false;
      }
      const salesStagePasses =
        !this.hcm.columnIsVisible('salesStage') || this.isSalesStageMenuItemChecked(entry.salesStage);
      if (!salesStagePasses) {
        console.log(`Filtering person ${entry.personId} failed on salesStage. Stage: **${entry.salesStage}**`);
        return false;
      }
    }

    const facilityPasses =
      !this.hcm.columnIsVisible('facilityName') || this.isFacilityMenuItemChecked(entry.facilityName);
    if (!facilityPasses) {
      console.log(`Filtering person ${entry.personId} failed on facilityName. Facility: **${entry.facilityName}**`);
      return false;
    }

    return true;
  }

  isOwnerMenuItemChecked(ownerId) {
    const foundOwner = this.ownerMenuItems.find((item) => item.id === ownerId);
    return foundOwner && foundOwner.checked;
  }

  isSalesStageMenuItemChecked(salesStage) {
    const actualStage = salesStage || 'Initial Contact';
    const foundSalesStage = this.salesStageMenuItems.find((item) => item.name === actualStage);
    return foundSalesStage && foundSalesStage.checked;
  }

  isTemperatureMenuItemChecked(leadTemperature) {
    const foundTemperature = this.temperatureMenuItems.find((item) => item.name === leadTemperature);
    return foundTemperature && foundTemperature.checked;
  }

  isFacilityMenuItemChecked(facilityName) {
    if (!this.props.emrInfo.isSyncedWithFirm) {
      return true;
    }
    const actualFacilityName = facilityName || 'Unassigned';
    if (this.facilityMenuItems.length > 0) {
      const foundFacility = this.facilityMenuItems.find((item) => item.name === actualFacilityName);
      return foundFacility && foundFacility.checked;
    } else {
      return true;
    }
  }

  showNewClientForm = () => {
    this.props.clearVobFormInfos();
    this.setState({ goToNewProspectPage: true });
  };

  englishListOfInclusions() {
    switch (this.props.shownStatus) {
      case 'Prospect':
        return 'Prospects';
      case 'Alum':
        return 'Alumni';
      case 'Client':
      default:
        return 'Clients';
    }
  }

  daysSinceContact(clientObject) {
    return DateTime.local().diff(clientObject.latestContactDate).days;
  }

  bestPhoneNumber(clientObject) {
    const phones = clientObject.phoneNumbers;
    if (!phones || phones.length === 0) {
      return '';
    }
    const firstMobile = phones.find((po) => po.roles && po.roles.indexOf('Mobile') >= 0);
    if (firstMobile) {
      const mobileNumber = firstMobile.number;
      return mobileNumber === '(000) 000-0000' ? '' : mobileNumber;
    }
    const candidate = phones[0].number;
    return candidate === '(000) 000-0000' ? null : candidate;
  }

  getOwnerNameOfClient(client) {
    const ownerId = client.ownerId;
    if (ownerId) {
      const owner = DA.getPersonById(ownerId);
      return owner ? owner.preferredFullName : 'Unassigned';
    } else {
      return 'Unassigned';
    }
  }

  augmentClient(client) {
    // Add facility name, owner name, maybe sales stage
    const emrClient = DA.getEmrProspectDetail(client.personId);
    const facilityName = emrClient ? emrClient.facilityName : 'Unassigned';

    // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
    const showDiagnosticLogging = false;
    if (showDiagnosticLogging) {
      if (facilityName === 'Unassigned') {
        if (emrClient) {
          console.log(
            `facilityName <- 'Unassigned' because DA.getEmrProspectDetail(${client.personId}).facilityName === 'Unassigned'`
          );
        } else {
          console.log(
            `facilityName <- 'Unassigned' because DA.getEmrProspectDetail(${client.personId}) returned null or undefined`
          );
        }
      }
    }

    const ownerName = this.getOwnerNameOfClient(client);

    const salesStage = client.salesStage || 'Initial Contact';

    return { ...client, facilityName, ownerName, salesStage };
  }

  handleColumnToggle(label, checked) {
    this.hcm.setVisibilityOfColumnWithLabel(label, checked);
    this.setState({ visibilityToggle: !this.state.visibilityToggle });
  }

  setOrderedHideableColumnIdsFromItems(permutationOfTextCheckedPairs) {
    this.hcm.setOrderedHideableColumnIdsFromItems(permutationOfTextCheckedPairs);
    this.setState({ visibilityToggle: !this.state.visibilityToggle });
  }

  downloadClients = (filteredClients, setDownloading) => {
    this.maxPhonesPerLine = 0;
    this.maxAddressesPerLine = 0;
    const { order, orderBy } = this.state;
    const clients = stableSort(filteredClients, getComparisonFunction(order, orderBy));
    this.fetchAndCombineVobInfo(clients, setDownloading);
  };

  fetchAndCombineVobInfo(clients, setDownloading) {
    const requests = [];
    clients.forEach((client) => {
      const clientId = client.personId;
      const uri = `/api/person/${clientId}/vobFormInfo`;
      const fetchRq = fetch(uri)
        .then((responstObj) => {
          return responstObj.json();
        })
        .then((json) => {
          return { client, vobFormInfos: json };
        });
      requests.push(fetchRq);
    });
    Promise.all(requests).then((res) =>
      this.convertMultipleClientsAndVobFormInfosToUseableFormAndContinue(res, setDownloading)
    );
  }

  convertMultipleClientsAndVobFormInfosToUseableFormAndContinue(clientsAndVobFormInfos, setDownloading) {
    const clientsAndVobInfos = clientsAndVobFormInfos.map((element) =>
      this.convertClientAndVobFormInfosToUseableForm(element)
    );
    this.continueDownloadClients(clientsAndVobInfos, setDownloading);
  }

  convertClientAndVobFormInfosToUseableForm(clientAndVobFormInfos) {
    const { client, vobFormInfos } = clientAndVobFormInfos;
    if (vobFormInfos.length === 0) {
      return { client, vobInfo: {} };
    } else {
      return { client, vobInfo: vobFormInfos[0] };
    }
  }

  continueDownloadClients = (clientsWithVobInfo, setDownloading) => {
    const lines = [];
    clientsWithVobInfo.forEach((clientWithVobInfo, i) => lines.push(this.lineForClient(clientWithVobInfo, i)));
    lines.unshift(this.createHeaderLine());
    const fileContent = lines.join('\n');

    const blob = new Blob([fileContent], { type: 'text/comma-separated-values' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = this.fileNameForShownStatus();
    link.click();
    URL.revokeObjectURL(url);
    link.remove();
    setDownloading(false);
  };

  fileNameForShownStatus() {
    const datePortion = DateTime.local().toFormat('yyyy-MM-dd--HH_mm_ss');
    switch (this.props.shownStatus) {
      case 'Prospect':
        return `prospects-${datePortion}.csv`;
      case 'Alum':
        return `alumni-${datePortion}.csv`;
      case 'Client':
      default:
        return `clients-${datePortion}.csv`;
    }
  }

  createHeaderLine() {
    const part1 = 'Count,First Name,Last Name,Email,Gender,Birth Date,Lead Temperature,Sales Stage,Facility';
    const part2 = ',Home Phone,Mobile Phone,Work Phone,Address Line1,Address Line2,City,State,Zip';
    const part3 =
      ',Vob Insurance Carrier,Vob Plan Type,Vob Group ID,Vob Policy ID,Vob First Name,Vob Last Name,Vob Releationship';
    const part4 = ',Vob Birth Date,Vob Street,Vob City,Vob State,Vob Zip';
    return `${part1}${part2}${part3}${part4}`;
  }

  lineForClient(clientWithVobInfo, i) {
    const person = clientWithVobInfo.client;
    const { bestMobile, bestHome, bestWork } = this.teaseOutBestPhoneNumbers(person);
    const firstName = this.csvEscapedValue(person.firstName);
    const lastName = this.csvEscapedValue(person.lastName);
    const email = this.csvEscapedValue(person.email);
    const gender = this.csvEscapedValue(person.sex);
    const birthDate = person.birthDate ? this.csvEscapedValue(person.birthDate.toFormat('MM/dd/yyyy')) : '';
    const temperature = this.csvEscapedValue(person.leadTemperature);
    const salesStage = this.csvEscapedValue(person.salesStage);
    const facility = this.csvEscapedValue(person.facilityName);

    const vobInfo = clientWithVobInfo.vobInfo;
    const vobInsuranceCarrier = this.csvEscapedValue(vobInfo.insuranceCarrier);
    const rawPlanType = vobInfo.planType;
    const vobPlanType = rawPlanType === 'Choose...' ? '' : this.csvEscapedValue(rawPlanType);
    const vobGroupId = this.csvEscapedValue(vobInfo.groupId);
    const vobPolicyId = this.csvEscapedValue(vobInfo.policyId);
    const vobHolderFirstName = this.csvEscapedValue(vobInfo.holderFirstName);
    const vobHolderLastName = this.csvEscapedValue(vobInfo.holderLastName);
    const rawRelationship = vobInfo.relationship;
    const vobRelationship = rawRelationship === 'Choose...' ? '' : this.csvEscapedValue(rawRelationship);
    const vobBirthDate = this.csvEscapedValue(vobInfo.birthDate);
    const vobHolderStreet = this.csvEscapedValue(vobInfo.holderStreet);
    const vobHolderCity = this.csvEscapedValue(vobInfo.holderCity);
    const rawState = vobInfo.holderState;
    const vobHolderState = rawState === 'Pick' || rawState === 'State' ? '' : this.csvEscapedValue(rawState);
    const vobHolderZip = this.csvEscapedValue(vobInfo.holderZip);

    let line = `${
      i + 1
    },${firstName},${lastName},${email},${gender},${birthDate},${temperature},${salesStage},${facility}`;
    line += `,${this.csvEscapedValue(bestHome)},${this.csvEscapedValue(bestMobile)},${this.csvEscapedValue(bestWork)}`;

    const addresses = person.addresses;
    if (addresses.length > 0) {
      const address = addresses[0];
      line += ',' + this.csvEscapedValue(address.line1);
      line += ',' + this.csvEscapedValue(address.line2);
      line += ',' + this.csvEscapedValue(address.city);
      line += ',' + this.csvEscapedValue(address.state);
      line += ',' + this.csvEscapedValue(address.zip);
    } else {
      line += ',,,,,';
    }

    line += `,${vobInsuranceCarrier},${vobPlanType},${vobGroupId},${vobPolicyId},${vobHolderFirstName},${vobHolderLastName},${vobRelationship}`;
    line += `,${vobBirthDate},${vobHolderStreet},${vobHolderCity},${vobHolderState},${vobHolderZip}`;

    return line;
  }

  teaseOutBestPhoneNumbers(person) {
    const numbers = person.phoneNumbers;
    const bestMobile = person.mobile || this.firstPhoneWithRole('Mobile', numbers);
    const bestHome = this.firstPhoneWithRole('Home', numbers);
    const bestWork = this.firstPhoneWithRole('Work', numbers) || this.firstPhoneWithRole('Office', numbers);
    return { bestMobile, bestHome, bestWork };
  }

  firstPhoneWithRole(role, numbers) {
    let found = false;
    let best = null;
    numbers.forEach((number) => {
      if (!found && number.roles.indexOf(role) >= 0) {
        found = true;
        best = number.number;
      }
    });
    return best;
  }

  csvEscapedValue(value) {
    if (!value) {
      return '';
    }
    let needsQuoteMarks = false;
    if (value.indexOf('"') >= 0) {
      needsQuoteMarks = true;
      value = value.replace('"', '""');
    }
    if (value.indexOf('\n') >= 0 || value.indexOf(',') >= 0) {
      needsQuoteMarks = true;
    }
    return needsQuoteMarks ? '"' + value + '"' : value;
  }

  computeSummaryText(showingProspects, filteredClients) {
    const filteringByOwner =
      this.hcm &&
      this.hcm.columnIsVisible('ownerName') &&
      !filterIsInDefaultState(this.ownerMenuItems, this.defaultOwnerMenuItems);
    const filteringByProspectColumn =
      this.hcm &&
      showingProspects &&
      ((this.hcm.columnIsVisible('leadTemperature') &&
        !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems)) ||
        (this.hcm.columnIsVisible('salesStage') &&
          !filterIsInDefaultState(this.salesStageMenuItems, this.defaultSalesStageMenuItems)));
    let filtered = filteringByOwner || filteringByProspectColumn;
    // do this conditionally so we don't have to evaluate filterIsInDefaultState most of the time.
    filtered =
      filtered ||
      (this.hcm &&
        this.hcm.columnIsVisible('facilityName') &&
        !filterIsInDefaultState(this.facilityMenuItems, this.defaultFacilityMenuItems));
    const quantifier = filtered ? 'Filtered' : 'All';
    const englishList = this.englishListOfInclusions();
    return `${quantifier} ${englishList} (${filteredClients.length})`;
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.goToNewProspectPage) {
      return <Redirect to="newProspectPage" push />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    const { classes, clientsInfo, shownStatus } = this.props;
    const { order, orderBy, filterItemList } = this.state;
    const allClients = clientsInfo.clientIds.map((id) => DA.getPersonById(id));
    const augmentedClients = allClients.map((client) => this.augmentClient(client));
    const filteredClients = this.applyFilter(augmentedClients);
    const showingProspects = shownStatus === 'Prospect';
    const summary = this.computeSummaryText(showingProspects, filteredClients);
    const newButtonLabel = showingProspects ? 'New Prospect' : undefined;
    const isFirmSyncedWithEmr = this.props.emrInfo.isSyncedWithFirm;
    const limitDate = DateTime.local().minus(Duration.fromObject({ years: 3 }));
    const StyledOwnerDialog = styled(Dialog)`
      .MuiBackdrop-root {
        background-color: transparent;
      },
    `;

    const filterPosition = {
      position: 'absolute',
      left: this.state.filterX + 'px',
      top: this.state.filterY + 'px',
      margin: '5px',
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    };
    const filterStyle = { style: filterPosition };

    const isAdmin = userIsAdmin();
    const hideableColumnNamesAndCheckedState = this.hcm ? this.hcm.getHideableColumnNamesAndCheckedState() : [];
    const visibleColumnDescrips = this.hcm ? this.hcm.visibleColumnDescrips : [];

    return (
      <div>
        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.deleteClientFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.deletionConfirmationQuery}</div>
        </ConfirmModal>

        <Paper className={classes.root}>
          <ClientTableAppBar
            summary={summary}
            newButtonLabel={newButtonLabel}
            newButtonFunc={() => this.showNewClientForm()}
            clients={filteredClients}
            onAutocompleteChange={(person) => this.handleClickOnClientName(person.personId)}
            hideableColumnNamesAndCheckedState={hideableColumnNamesAndCheckedState}
            onColumnToggle={(label, checked) => this.handleColumnToggle(label, checked)}
            onColumnReorder={(permutation) => this.setOrderedHideableColumnIdsFromItems(permutation)}
            downloadFunc={(setDownloading) => this.downloadClients(filteredClients, setDownloading)}
            shownStatus={this.props.shownStatus}
          />
          <div className={classes.tableWrapper}>
            <Table stickyHeader className={classes.table} aria-labelledby="tableTitle">
              <StyledTableHead
                hcm={this.hcm}
                order={order}
                orderBy={orderBy}
                onRequestSort={this.handleRequestSort}
                shownStatus={shownStatus}
                onRequestFilter={this.showFilterMenu}
                isFirmSyncedWithEmr={isFirmSyncedWithEmr}
                shouldHighlightFilterIcon={(columnId) => this.shouldHighlightFilterIcon(columnId)}
              />
              <TableBody>
                {stableSort(filteredClients, getComparisonFunction(order, orderBy)).map((client, i) => {
                  client.mobile = this.bestPhoneNumber(client);
                  return (
                    <TableRow
                      tabIndex={-1}
                      key={client.personId}
                      id={'client-row-' + client.personId}
                      style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                    >
                      {visibleColumnDescrips.map((columnDescrip, j) => {
                        const columnId = columnDescrip.id;
                        switch (columnId) {
                          case 'preferredName':
                            return (
                              <TableCell
                                key={columnId}
                                className={classes.clickableCell}
                                component="th"
                                scope="row"
                                padding="normal"
                                onClick={() => this.handleClickOnClientName(client.personId)}
                              >
                                {client.preferredName}
                              </TableCell>
                            );
                          case 'lastName':
                            return (
                              <TableCell
                                key={columnId}
                                className={classes.clickableCell}
                                onClick={() => this.handleClickOnClientName(client.personId)}
                              >
                                {client.lastName || ''}
                              </TableCell>
                            );
                          case 'createdDate':
                            return <TableCell key={columnId}>{dateStringForDateTime(client.createdDate)}</TableCell>;
                          case 'phone':
                            return <TableCell key={columnId}>{client.mobile}</TableCell>;
                          case 'leadTemperature':
                            return showingProspects && <TableCell key={columnId}>{client.leadTemperature}</TableCell>;
                          case 'salesStage':
                            return (
                              showingProspects && (
                                <TableCell key={columnId}>{client.salesStage || 'Initial Contact'}</TableCell>
                              )
                            );
                          case 'facilityName':
                            return isFirmSyncedWithEmr && <TableCell key={columnId}>{client.facilityName}</TableCell>;
                          case 'ownerName':
                            return <TableCell key={columnId}>{client.ownerName}</TableCell>;
                          case 'latestContactDate':
                            return (
                              <TableCell key={columnId} align="right">
                                <Tooltip className={classes.colLatestContact} title={client.latestContactDescription}>
                                  <span>{dateStringForDateTimeIfLaterThan(client.latestContactDate, limitDate)}</span>
                                </Tooltip>
                              </TableCell>
                            );
                          case 'actions':
                            return (
                              isAdmin && (
                                <TableCell key={columnId}>
                                  <Tooltip
                                    id="tooltip-delete"
                                    title={'Delete ' + client.preferredName}
                                    placement="top"
                                    classes={{ tooltip: classes.tooltip }}
                                  >
                                    <IconButton
                                      className={classes.actionButton}
                                      onClick={() => this.handleClickOnDeleteIcon(client.personId)}
                                      size="large"
                                    >
                                      <TrashcanIcon className={classes.icon} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )
                            );
                          default:
                            return null;
                        }
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Paper>

        <StyledOwnerDialog
          open={this.state.showFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={() => this.handleFilterClickAway()}>
            <List>
              {filterItemList.map((item, i) => (
                <ListItem className={classes.filterListItem} key={i}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {i === 0 && (
                      <Button
                        className={classes.selectAllButton}
                        onClick={() => this.handleSelectAllButtonClick(item.name)}
                      >
                        {item.name}
                      </Button>
                    )}
                    {i > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.checked}
                            onChange={() => this.handleFilterItemChange(i)}
                            value={item.name}
                            color="primary"
                          />
                        }
                        label={item.name}
                        className={classes.filterLabel}
                      />
                    )}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </StyledOwnerDialog>
      </div>
    );
  }
}

ClientTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  clientsInfo: state.clientsInfo,
  emrInfo: state.emrInfo,
  reduxFilterMenuState: state.intendedInfo.clientsPageFilter,
  reduxPropertyFiltersState: state.filterStates.propertyFilters,
});

const actionProducers = {
  setClientsInfo,
  addPersons,
  removeClientFromClientsInfo,
  setApiResponseInfo,
  setCaseIntendedClientId,
  updateClientsFilter,
  clearVobFormInfos,
  setPropertyFilterState,
  setPersonFilterState,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(ClientTable));
